import React, { useCallback, useMemo, useState } from 'react';

import { Box, Button, Typography } from '@mui/material';
import { CopyIcon } from '@sharedCR/coreDesignSystem/icons';
import SecondaryTabGroupCDS from '@sharedCR/coreDesignSystem/SecondaryTabGroupCDS';
import _ from 'lodash';
import PropTypes from 'prop-types';

import { IconSize } from 'constants/coreDesignSystem';

import {
  COPY_ID_LABEL,
  COPY_ID_SUCCESS_MESSAGE,
  COPY_JSON_LABEL,
  COPY_JSON_SUCCESS_MESSAGE,
  DETAIL_LOADING_LABEL,
  DETAIL_RETRY_LABEL,
  DETAIL_TAB_LABELS,
  DETAIL_UNAVAILABLE_MESSAGE,
  DetailTab,
  EMPTY_FIELD_LIST,
  getFieldsDifferNote,
  NO_FIELD_CHANGES_NOTE,
  SNAPSHOT_AFTER_LABEL,
  SNAPSHOT_BEFORE_LABEL,
} from '../../EntityAuditHistory.constants';
import {
  AuditChangePanelsPropType,
  AuditFieldListPropType,
  AuditFieldRenderersPropType,
  AuditRenderersPropType,
} from '../../EntityAuditHistory.propTypes';
import {
  detailHeaderStyles,
  detailTabsRowStyles,
  getSnapshotLineStyles,
  snapshotBlockStyles,
  snapshotLineStyles,
  snapshotPaneStyles,
  snapshotPanesStyles,
  snapshotToolbarStyles,
} from '../../EntityAuditHistory.styles';
import { EntityAuditHistoryTestIds } from '../../EntityAuditHistory.testIds';
import {
  buildDetailExtras,
  countDifferingFields,
  createFieldMatcher,
  getOnBehalfOfNote,
  getSnapshotLines,
  omitSnapshotFields,
  prettyPrintSnapshot,
  toOperation,
  withoutHiddenChanges,
} from '../../EntityAuditHistory.utils';
import useCopyWithNotice from '../../hooks/useCopyWithNotice';
import AuditDetailChanges from '../AuditDetailChanges';
import AuditStateMessage from '../AuditStateMessage';

const DETAIL_TABS = [
  { label: DETAIL_TAB_LABELS[DetailTab.CHANGES], value: DetailTab.CHANGES },
  { label: DETAIL_TAB_LABELS[DetailTab.SNAPSHOT], value: DetailTab.SNAPSHOT },
];

const AuditRecordDetail = ({
  auditId,
  detail,
  operation = null,
  actorNames = null,
  isLoading = false,
  isUnavailable = false,
  row = null,
  renderers = null,
  fieldRenderers = null,
  hiddenFields = EMPTY_FIELD_LIST,
  snapshotExcludedFields = EMPTY_FIELD_LIST,
  renderDetailExtras = null,
  changePanels = null,
  isSnapshotHidden = false,
  automaticFields = EMPTY_FIELD_LIST,
  onRetry,
}) => {
  const copyWithNotice = useCopyWithNotice();

  const [activeTab, setActiveTab] = useState(DetailTab.CHANGES);

  const onTabChange = useCallback(value => setActiveTab(value), []);

  const changes = useMemo(
    () => withoutHiddenChanges(detail?.changes ?? [], hiddenFields),
    [detail, hiddenFields],
  );
  const changedFieldNames = useMemo(() => changes.map(change => change.field), [changes]);

  // A summarised field's raw value was withheld server-side; hidden and snapshot-excluded fields are
  // the consumer's choice. None of them belong in the before/after panes or the copied JSON.
  const snapshot = useMemo(() => {
    const isExcluded = createFieldMatcher(
      hiddenFields,
      snapshotExcludedFields,
      detail?.summarizedFields,
    );

    return {
      before: omitSnapshotFields(detail?.before, isExcluded),
      after: omitSnapshotFields(detail?.after, isExcluded),
    };
  }, [detail, hiddenFields, snapshotExcludedFields]);

  const extras = useMemo(
    () => buildDetailExtras(renderDetailExtras, row, detail),
    [renderDetailExtras, row, detail],
  );

  const tabs = useMemo(
    () => [
      ...DETAIL_TABS.filter(tab => !isSnapshotHidden || tab.value !== DetailTab.SNAPSHOT),
      ...extras.map(({ label, value }) => ({ label, value })),
    ],
    [isSnapshotHidden, extras],
  );

  const activeExtra = extras.find(extra => extra.value === activeTab) ?? null;
  const isSnapshotVisible = activeTab === DetailTab.SNAPSHOT && !isSnapshotHidden;
  const visibleTab = activeExtra || isSnapshotVisible ? activeTab : DetailTab.CHANGES;

  // Prefer the operation from the reliable index summary (passed by the row); the detail DTO's
  // own summary.operation is the fallback. Without this, a detail missing that field would render a
  // Create/Delete as a two-sided Was->Became diff with nulls.
  const resolvedOperation = operation ?? toOperation(detail?.summary?.operation);

  const onCopyId = useCallback(
    () => copyWithNotice(auditId, COPY_ID_SUCCESS_MESSAGE),
    [copyWithNotice, auditId],
  );
  const onCopyJson = useCallback(
    () => copyWithNotice(prettyPrintSnapshot(snapshot), COPY_JSON_SUCCESS_MESSAGE),
    [copyWithNotice, snapshot],
  );

  if (isLoading) {
    return (
      <AuditStateMessage
        isLoading
        dataTestId={EntityAuditHistoryTestIds.DETAIL_LOADING}
        isFramed={false}
        message={DETAIL_LOADING_LABEL}
      />
    );
  }

  if (isUnavailable || !detail) {
    return (
      <AuditStateMessage
        dataTestId={EntityAuditHistoryTestIds.DETAIL_UNAVAILABLE}
        isFramed={false}
        message={DETAIL_UNAVAILABLE_MESSAGE}
        retryLabel={DETAIL_RETRY_LABEL}
        retryTestId={EntityAuditHistoryTestIds.DETAIL_RETRY_BUTTON}
        onRetry={onRetry}
      />
    );
  }

  const onBehalfOf = getOnBehalfOfNote(detail.summary, actorNames);
  const hasBefore = !_.isNil(snapshot.before);
  const hasAfter = !_.isNil(snapshot.after);
  const hasSnapshot = hasBefore || hasAfter;

  return (
    <Box data-testid={EntityAuditHistoryTestIds.DETAIL}>
      {!!onBehalfOf && (
        <Box data-testid={EntityAuditHistoryTestIds.DETAIL_HEADER} sx={detailHeaderStyles}>
          <Typography
            color="text.secondary"
            data-testid={EntityAuditHistoryTestIds.DETAIL_ON_BEHALF_OF}
            variant="regular14"
          >
            {onBehalfOf}
          </Typography>
        </Box>
      )}

      {tabs.length > 1 && (
        <Box sx={detailTabsRowStyles}>
          <SecondaryTabGroupCDS
            buttons={tabs}
            data-testid={EntityAuditHistoryTestIds.DETAIL_TABS}
            value={visibleTab}
            onChange={onTabChange}
          />

          {visibleTab === DetailTab.SNAPSHOT && (
            <Box sx={snapshotToolbarStyles}>
              <Typography
                color="text.secondary"
                data-testid={EntityAuditHistoryTestIds.SNAPSHOT_DIFFER_NOTE}
                variant="regular12"
              >
                {getFieldsDifferNote(countDifferingFields(changes))}
              </Typography>
              <Button
                data-testid={EntityAuditHistoryTestIds.SNAPSHOT_COPY_ID}
                size="small"
                startIcon={<CopyIcon size={IconSize[16]} />}
                variant="outlined"
                onClick={onCopyId}
              >
                {COPY_ID_LABEL}
              </Button>
              <Button
                data-testid={EntityAuditHistoryTestIds.SNAPSHOT_COPY_JSON}
                disabled={!hasSnapshot}
                size="small"
                startIcon={<CopyIcon size={IconSize[16]} />}
                variant="outlined"
                onClick={onCopyJson}
              >
                {COPY_JSON_LABEL}
              </Button>
            </Box>
          )}
        </Box>
      )}

      {visibleTab === DetailTab.CHANGES && (
        <AuditDetailChanges
          automaticFields={automaticFields}
          changePanels={changePanels}
          changes={changes}
          detail={detail}
          fieldRenderers={fieldRenderers}
          keyPrefix={auditId}
          operation={resolvedOperation}
          renderers={renderers}
          row={row}
        />
      )}

      {visibleTab === DetailTab.SNAPSHOT &&
        (hasSnapshot ? (
          <Box data-testid={EntityAuditHistoryTestIds.SNAPSHOT_PANEL} sx={snapshotPanesStyles}>
            {hasBefore && (
              <Box sx={snapshotPaneStyles}>
                <Typography color="text.secondary" variant="medium12">
                  {SNAPSHOT_BEFORE_LABEL}
                </Typography>
                <Typography
                  component="pre"
                  data-testid={EntityAuditHistoryTestIds.SNAPSHOT_BEFORE}
                  sx={snapshotBlockStyles}
                  variant="regular12"
                >
                  {getSnapshotLines(snapshot.before, changedFieldNames).map(line => (
                    <Box
                      component="span"
                      key={line.key}
                      sx={line.isChanged ? getSnapshotLineStyles(true) : snapshotLineStyles}
                    >
                      {`${line.text}\n`}
                    </Box>
                  ))}
                </Typography>
              </Box>
            )}
            {hasAfter && (
              <Box sx={snapshotPaneStyles}>
                <Typography color="text.secondary" variant="medium12">
                  {SNAPSHOT_AFTER_LABEL}
                </Typography>
                <Typography
                  component="pre"
                  data-testid={EntityAuditHistoryTestIds.SNAPSHOT_AFTER}
                  sx={snapshotBlockStyles}
                  variant="regular12"
                >
                  {getSnapshotLines(snapshot.after, changedFieldNames).map(line => (
                    <Box
                      component="span"
                      key={line.key}
                      sx={line.isChanged ? getSnapshotLineStyles(false) : snapshotLineStyles}
                    >
                      {`${line.text}\n`}
                    </Box>
                  ))}
                </Typography>
              </Box>
            )}
          </Box>
        ) : (
          <Typography color="text.secondary" variant="regular14">
            {NO_FIELD_CHANGES_NOTE}
          </Typography>
        ))}

      {!!activeExtra && (
        <Box data-testid={EntityAuditHistoryTestIds.DETAIL_EXTRA_PANEL}>{activeExtra.content}</Box>
      )}
    </Box>
  );
};

AuditRecordDetail.propTypes = {
  auditId: PropTypes.string.isRequired,
  onRetry: PropTypes.func.isRequired,
  detail: PropTypes.object,
  operation: PropTypes.string,
  actorNames: PropTypes.object,
  isLoading: PropTypes.bool,
  isUnavailable: PropTypes.bool,
  row: PropTypes.object,
  renderers: AuditRenderersPropType,
  fieldRenderers: AuditFieldRenderersPropType,
  hiddenFields: AuditFieldListPropType,
  snapshotExcludedFields: AuditFieldListPropType,
  renderDetailExtras: PropTypes.func,
  changePanels: AuditChangePanelsPropType,
  isSnapshotHidden: PropTypes.bool,
  automaticFields: AuditFieldListPropType,
};

export default AuditRecordDetail;
